import apiClient from './api'
import { withListDefaults } from '@/lib/listFetch'
import type {
  ApiResponse,
  ApiList,
  ListQuery,
  ExportLink,
  Technician,
  TechnicianDetail,
  TechnicianListQuery,
  TechnicianAvailability,
  CreateTechnicianRequest,
  EditTechnicianRequest,
  AvailabilityRequest,
  Documentable,
  Job,
  JobReport,
} from '@/types'

// 1:1 with Schema_Specification Section 4.10 (technicians / 2.10.x).
export const techniciansApi = {
  // 2.10.1
  list: (q: TechnicianListQuery = {}) =>
    apiClient
      .get<ApiList<Technician>>('/technicians', { params: withListDefaults(q) })
      .then(r => r.data),

  // List export (feedback #84). Honours the current search/status/skill/city
  // filters; returns a signed download link (Technician_list_[YYYY-MM-DD].[ext]).
  export: (q: TechnicianListQuery & { format?: 'xlsx' | 'csv' } = {}) =>
    apiClient
      .get<ApiResponse<ExportLink>>('/technicians/export', { params: q })
      .then(r => r.data),

  // 2.10.1 — options of the list "Skills" filter: the service categories
  // defined in Settings (2.11.5), readable without the Admin-only CRUD route.
  serviceCategoryOptions: () =>
    apiClient
      .get<
        ApiResponse<{ id: string; name: string }[]>
      >('/technicians/service-category-options')
      .then(r => r.data),

  // 2.10.5
  get: (id: string) =>
    apiClient
      .get<ApiResponse<TechnicianDetail>>(`/technicians/${id}`)
      .then(r => r.data),

  // 2.10.3
  create: (body: CreateTechnicianRequest) =>
    apiClient
      .post<ApiResponse<Technician>>('/technicians', body)
      .then(r => r.data),

  // 2.10.3 — resend welcome / credential email (rotates temp password).
  resendWelcomeEmail: (id: string) =>
    apiClient
      .post<
        ApiResponse<TechnicianDetail>
      >(`/technicians/${id}/resend-welcome-email`)
      .then(r => r.data),

  // 2.10.6 / feedback #82 — append a timestamped note to the technician.
  addNote: (id: string, note: string) =>
    apiClient
      .post<ApiResponse<TechnicianDetail>>(`/technicians/${id}/notes`, { note })
      .then(r => r.data),

  // 2.10.4
  update: (id: string, body: EditTechnicianRequest) =>
    apiClient
      .put<ApiResponse<Technician>>(`/technicians/${id}`, body)
      .then(r => r.data),

  // 2.10.7
  deactivate: (id: string) =>
    apiClient
      .put<ApiResponse<Technician>>(`/technicians/${id}/deactivate`)
      .then(r => r.data),

  // 2.10.7 — dedicated reactivate endpoint (the standard edit endpoint requires
  // the full technician payload and 422s on a partial { status } body).
  activate: (id: string) =>
    apiClient
      .put<ApiResponse<Technician>>(`/technicians/${id}/activate`)
      .then(r => r.data),

  // 2.10.8
  remove: (id: string) =>
    apiClient.delete<ApiResponse<null>>(`/technicians/${id}`).then(r => r.data),

  // 2.10.2
  availability: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<
        ApiList<TechnicianAvailability>
      >(`/technicians/${id}/availability`, { params: withListDefaults(q) })
      .then(r => r.data),

  createAvailability: (id: string, body: AvailabilityRequest) =>
    apiClient
      .post<
        ApiResponse<TechnicianAvailability>
      >(`/technicians/${id}/availability`, body)
      .then(r => r.data),

  updateAvailability: (id: string, avId: string, body: AvailabilityRequest) =>
    apiClient
      .put<
        ApiResponse<TechnicianAvailability>
      >(`/technicians/${id}/availability/${avId}`, body)
      .then(r => r.data),

  deleteAvailability: (id: string, avId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/technicians/${id}/availability/${avId}`)
      .then(r => r.data),

  // 2.10.6
  jobs: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<Job>>(`/technicians/${id}/jobs`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  reports: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<JobReport>>(`/technicians/${id}/reports`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  // 2.10.6 — documents tab.
  documents: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<Documentable>>(`/technicians/${id}/documents`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  uploadDocument: (id: string, file: File, docType?: string) => {
    const form = new FormData()
    form.append('file', file)
    if (docType) form.append('doc_type', docType)
    return apiClient
      .post<ApiResponse<Documentable>>(`/technicians/${id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      })
      .then(r => r.data)
  },

  deleteDocument: (id: string, documentId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/technicians/${id}/documents/${documentId}`)
      .then(r => r.data),

  renameDocument: (id: string, documentId: string, name: string) =>
    apiClient
      .patch<
        ApiResponse<Documentable>
      >(`/technicians/${id}/documents/${documentId}/rename`, { name })
      .then(r => r.data),
}

export default techniciansApi
